import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorMissing, ensurePanel } from './tasks-help.shared';

/**
 * ?-Hilfe fuer die Aufgaben-Uebersicht (`/tasks/overview`).
 *
 * Die Seite ist die **Manager-Sicht**: sie zeigt alles, was der Angemeldete
 * sehen darf — nicht das, wofuer er zustaendig ist. Wer hier seine eigene
 * Liste sucht, ist auf "Meine Aufgaben" richtig. Das traegt Schritt 1 und ist
 * die haeufigste Rueckfrage zu dieser Flaeche.
 *
 * Belege im Code (fuer spaetere Reviews):
 *  - Manager- vs. Eigen-Sicht: `modules/tasks/README.md` ("Sichtbarkeit ≠
 *    Verantwortung") und die eigene Seite `pages/my-tasks-page.component.ts`
 *  - Kennzahl-Pillen sind Filter mit SERVERSEITIGER Zahl, "Diese Woche" ist ein
 *    rollendes 7-Tage-Fenster ab morgen: `pages/overview/overview-stats.ts`
 *  - Gruppenkoepfe = Dringlichkeitsstufen, "Verpasst" ist eine EIGENE Stufe und
 *    liegt bewusst nicht unter "Erledigt": `pages/overview/overview-grouping.ts`
 *  - "Geplant aus Routinen" ist eine Vorschau auf den naechtlichen Lauf, die
 *    Aufgaben erscheinen erst zu ihrer "Sichtbar ab"-Zeit:
 *    `pages/overview/overview-board.ts` + `pages/routines/…`
 *
 * Seiten-Tour, also KEIN `dialogContext`. `launchContext.route` erlaubt den
 * Start aus dem Hilfe-Hub; der ?-Knopf im Seitenkopf startet sie direkt.
 *
 * Sechs der zehn Schritte haengen an Bestand, Rechten oder Panel-Zustand
 * (Erstnutzung: weder Kennzahlen- noch Filterband stehen im DOM). Deshalb
 * `optional` + `skipIf` und `renumberVisibleSteps` — sonst zaehlte das Popover
 * "2 / 10 → 5 / 10" und der Nutzer sucht die fehlenden Schritte.
 */
export const TASKS_OVERVIEW_HELP_TOUR: TourConfig = {
  id: TourFeature.TasksOverviewHelp,
  version: 1,
  title: 'TASKS_OVERVIEW_HELP_TOUR_TITLE',
  description: 'TASKS_OVERVIEW_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  launchContext: {
    route: '/tasks/overview',
    mode: 'auto',
  },
  triggerContext: {
    route: '/tasks/overview',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 170,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASKS_OVERVIEW_HELP_TOUR_TITLE',
      steps: [
        {
          // Kern-Step: Manager-Sicht, nicht die eigene Aufgabenliste.
          id: 'ov-welcome',
          anchor: AnchorId.TasksPage,
          spotlightAnchor: AnchorId.TasksPage,
          title: 'TUTORIAL.TASKS_OVERVIEW.WELCOME.TITLE',
          content: 'TUTORIAL.TASKS_OVERVIEW.WELCOME.CONTENT',
          placement: 'bottom',
          align: 'start',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          // Die Reiter: was ist offen (Uebersicht), was wiederholt sich
          // (Routinen), wie steht es (Lage, fuehrt zu den Nachweisen), wo liegt
          // die Arbeit (Durchlauf), wer hat was erledigt (Auswertung).
          // `-v3` seit 10.10.2026 (Lage statt Compliance), bis zur Vertonung stumm.
          id: 'ov-tabs',
          anchor: AnchorId.TasksTabs,
          spotlightAnchor: AnchorId.TasksTabs,
          title: 'TUTORIAL.TASKS_OVERVIEW.TABS.TITLE',
          content: 'TUTORIAL.TASKS_OVERVIEW.TABS.CONTENT',
          audioStepId: 'ov-tabs-v3',
          placement: 'bottom',
          align: 'start',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          // Kennzahlen-Pillen sind Filter, keine reine Anzeige. Im
          // Erstnutzungs-Zustand fehlt die ganze Leiste (`!showFirstUse()`).
          id: 'ov-pills',
          anchor: AnchorId.TasksStatStrip,
          spotlightAnchor: AnchorId.TasksStatStrip,
          additionalSpotlightAnchors: [
            AnchorId.TasksChipToday,
            AnchorId.TasksChipWeek,
          ],
          title: 'TUTORIAL.TASKS_OVERVIEW.PILLS.TITLE',
          content: 'TUTORIAL.TASKS_OVERVIEW.PILLS.CONTENT',
          audioStepId: 'ov-pills-v2',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksStatStrip),
          // Rueckweg von `ov-rail`: die Leiste wieder in den Ausgangszustand.
          onBeforeShow: () =>
            ensurePanel(AnchorId.TasksRailToggle, AnchorId.TasksViewRail, false),
        },
        {
          // Der Popover haengt am Toggle, der Spotlight an der Leiste.
          // ⚠️ Befund D-1 (06.10.2026): `<app-tasks-view-rail>` steht IMMER im DOM,
          // auch zugeklappt — die Existenzpruefung hielt sie fuer offen, und die
          // Station erklaerte einen schmalen Streifen. Die Seite meldet den Zustand
          // deshalb als `data-open` am Anker, `ensurePanel` liest ihn (idempotent).
          // Den Ausgangszustand stellt die Seite beim Tour-Ende wieder her
          // (`tasks-overview-page.component.ts`, `watchHelpTour`).
          //
          // ⚠️ T-31: Auf dem Telefon steht der Umschalter seit 13.08.2026 in einem
          // Ueberlaufmenue und ist bei geschlossenem Menue NICHT im DOM. Der Schritt
          // KLICKT den Anker (`ensurePanel`) — ohne die Absicherung braeche die Tour
          // dort ab. Deshalb ueberspringbar wie `ov-filters`/`ov-quickadd`, statt den
          // Anker unsichtbar im DOM zu halten (ein versteckter Anker liesse `skipIf`
          // ins Leere greifen, siehe die xds-section-Lektion).
          id: 'ov-rail',
          anchor: AnchorId.TasksRailToggle,
          spotlightAnchor: AnchorId.TasksViewRail,
          additionalSpotlightAnchors: [AnchorId.TasksViewSwitch],
          title: 'TUTORIAL.TASKS_OVERVIEW.RAIL.TITLE',
          content: 'TUTORIAL.TASKS_OVERVIEW.RAIL.CONTENT',
          audioStepId: 'ov-rail-v2',
          placement: 'right',
          align: 'start',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksRailToggle),
          onBeforeShow: () =>
            ensurePanel(AnchorId.TasksRailToggle, AnchorId.TasksViewRail, true),
        },
        {
          // Der Status-Filter haengt an `*ngIf="!railOpen()"` — die
          // Sichten-Leiste MUSS vorher wieder zu sein, sonst erklaert dieser
          // Schritt ein Feld, das gar nicht im DOM steht.
          //
          // Rueckweg von `ov-quickadd`: auch die Schnellerfassung wieder zu.
          //
          // Warum das Schliessen HIER steht und nicht im `onAfterHide` des
          // Rail-Schritts: `onAfterHide` feuert auf Vorwaerts-Transitionen
          // nicht zuverlaessig — dokumentierte Lektion aus
          // `TimeRecordingDetailsHelp`, siehe
          // `docs/onboarding-refactor/CONTEXTUAL_HELP_PATTERN.md`
          // ("Aufraeum-Aktion zusaetzlich im onBeforeShow des Folge-Steps
          // absichern").
          id: 'ov-filters',
          anchor: AnchorId.TasksFilterBar,
          spotlightAnchor: AnchorId.TasksFilterBar,
          title: 'TUTORIAL.TASKS_OVERVIEW.FILTERS.TITLE',
          content: 'TUTORIAL.TASKS_OVERVIEW.FILTERS.CONTENT',
          audioStepId: 'ov-filters-v2',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksFilterBar),
          onBeforeShow: async () => {
            await ensurePanel(
              AnchorId.TasksRailToggle,
              AnchorId.TasksViewRail,
              false
            );
            await ensurePanel(
              AnchorId.TasksQuickAddToggle,
              AnchorId.TasksQuickAdd,
              false
            );
          },
        },
        {
          // Das Schnell-Anlegen-Feld existiert erst nach dem Aufklappen —
          // `skipIf` prueft deshalb den TOGGLE, nicht das Panel.
          // ⚠️ Befund D-2 (06.10.2026): der Toggle ist ein `xds-icon-button`; ein
          // `click()` an dessen Host erreichte den inneren Knopf nie, das Feld blieb
          // zu. `ensurePanel` klickt seitdem das Bedienelement IM Anker.
          id: 'ov-quickadd',
          anchor: AnchorId.TasksQuickAddToggle,
          spotlightAnchor: AnchorId.TasksQuickAdd,
          title: 'TUTORIAL.TASKS_OVERVIEW.QUICK_ADD.TITLE',
          content: 'TUTORIAL.TASKS_OVERVIEW.QUICK_ADD.CONTENT',
          audioStepId: 'ov-quickadd-v2',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksQuickAddToggle),
          onBeforeShow: () =>
            ensurePanel(
              AnchorId.TasksQuickAddToggle,
              AnchorId.TasksQuickAdd,
              true
            ),
        },
        {
          // Kern-Step: Gruppenkoepfe sind Dringlichkeitsstufen. "Verpasst" ist
          // eine eigene Stufe und liegt bewusst NICHT unter "Erledigt".
          // Vorher das Schnell-Anlegen-Feld wieder schliessen (siehe die
          // onAfterHide-Begruendung am Filter-Schritt).
          id: 'ov-groups',
          anchor: AnchorId.TasksGroupHead,
          spotlightAnchor: AnchorId.TasksTable,
          title: 'TUTORIAL.TASKS_OVERVIEW.GROUPS.TITLE',
          content: 'TUTORIAL.TASKS_OVERVIEW.GROUPS.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksTable),
          onBeforeShow: () =>
            ensurePanel(
              AnchorId.TasksQuickAddToggle,
              AnchorId.TasksQuickAdd,
              false
            ),
        },
        {
          // Tour-Zeile = erste Zeile MIT Abhak-Kreis, sonst die erste Zeile
          // (`tourRowId` in der Seite). Vorher stand der Anker fest an der ersten
          // Zeile — hatte die eine Checkliste, lief der Kreis-Cue ins Leere.
          id: 'ov-row',
          anchor: AnchorId.TasksRowFirst,
          spotlightAnchor: AnchorId.TasksRowFirst,
          title: 'TUTORIAL.TASKS_OVERVIEW.ROW.TITLE',
          content: 'TUTORIAL.TASKS_OVERVIEW.ROW.CONTENT',
          audioStepId: 'ov-row-v2',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksRowFirst),
        },
        {
          // Nur mit `ManageRoutines` UND Routinen-Bestand im DOM.
          id: 'ov-planned',
          anchor: AnchorId.TasksPlannedHead,
          spotlightAnchor: AnchorId.TasksPlannedGroup,
          title: 'TUTORIAL.TASKS_OVERVIEW.PLANNED.TITLE',
          content: 'TUTORIAL.TASKS_OVERVIEW.PLANNED.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksPlannedGroup),
        },
        {
          // Beide Anlege-Wege fuehren in dasselbe Formular: der Kopf-Knopf und
          // die Erstnutzungs-Kacheln. Zur Laufzeit steht immer nur einer der
          // beiden Anker im DOM (`showFirstUse()`), der Cue auf den anderen
          // laeuft still ins Leere.
          id: 'ov-create',
          anchor: AnchorId.TasksCreateButton,
          spotlightAnchor: AnchorId.TasksCreateButton,
          additionalSpotlightAnchors: [AnchorId.TasksFirstUse],
          title: 'TUTORIAL.TASKS_OVERVIEW.CREATE.TITLE',
          content: 'TUTORIAL.TASKS_OVERVIEW.CREATE.CONTENT',
          placement: 'bottom',
          align: 'end',
          nextCondition: 'manual',
          showNextButton: true,
          actions: [
            {
              labelKey: 'TUTORIAL.TASKS_OVERVIEW.CREATE.ACTION',
              icon: 'fa-light fa-plus',
              startTour: TourFeature.TaskSheetHelp,
              variant: 'primary',
            },
          ],
        },
      ],
    },
  ],
};
